import { useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "react-simple-snackbar";
import { appState } from "$store";
import { IBadgeStatus } from "$components/JBadge/JBadge";
import { snackbarSuccessOptions } from "$utils/SnackbarOptions";
import { useStyles } from "$containers/Orders/Orders.styles";
import { OrderStatus, SuborderType, SUBORDER_TYPE_LABELS } from "$types";

export const STATUS_ORDERS: IBadgeStatus = {
  in_progress: "En cours",
  [OrderStatus.TO_DELIVERED]: "En cours",
  [OrderStatus.CANCELED]: "Annulée",
  [OrderStatus.DELIVERED_INCOMPLETE]: "Livré partiel",
  done: "Terminée",
  [OrderStatus.DELIVERED_OK]: "Livré total",
  new: "Nouveau",
};

const useOrder = () => {
  const { filter } = useSelector(appState);

  const classes = useStyles();

  // states
  const [open, setOpen] = useState<boolean>(false);
  const [dateStart_tfilter, setDateStart_tfilter] = useState<
    Date | undefined | null
  >(null);
  const [dateEnd_tfilter, setDateEnd_tfilter] = useState<
    Date | undefined | null
  >(null);

  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);

  const getOrderType = (suborders: any) => {
    let label = "";
    let countTypes = 0;

    if (suborders) {
      if (suborders.find((sub: any) => sub.type === SuborderType.DOCKYARD)) {
        label = SUBORDER_TYPE_LABELS[SuborderType.DOCKYARD];
        countTypes += 1;
      }
      if (suborders.find((sub: any) => sub.type === SuborderType.PROJECT)) {
        label = SUBORDER_TYPE_LABELS[SuborderType.PROJECT];
        countTypes += 1;
      }
      if (suborders.find((sub: any) => sub.type === SuborderType.STOCK)) {
        label = SUBORDER_TYPE_LABELS[SuborderType.STOCK];
        countTypes += 1;
      }

      if (countTypes > 1) {
        label = "Multiple";
      }
    }

    return label;
  };

  const getNewOrderType = (type: any) => {
    let label = "Aucun";

    if (type) {
      if (type?.includes(",")) {
        label = "Multiple";
      } else if (type === SuborderType.PROJECT) {
        label = SUBORDER_TYPE_LABELS[SuborderType.PROJECT];
      } else if (type === SuborderType.STOCK) {
        label = SUBORDER_TYPE_LABELS[SuborderType.STOCK];
      } else if (type === SuborderType.DOCKYARD) {
        label = SUBORDER_TYPE_LABELS[SuborderType.DOCKYARD];
      } else {
        label = "Aucun";
      }
    }

    return label;
  };

  const getSubOrderType = (type: any) => {
    let label = "";
    if (type === SuborderType.DOCKYARD) {
      label = SUBORDER_TYPE_LABELS[SuborderType.DOCKYARD];
    }
    if (type === SuborderType.PROJECT) {
      label = SUBORDER_TYPE_LABELS[SuborderType.PROJECT];
    }
    if (type === SuborderType.STOCK) {
      label = SUBORDER_TYPE_LABELS[SuborderType.STOCK];
    }
    return label;
  };

  const getSuborderTypeInfo = (type: string, dockyard: any, project: any) => {
    const { label, code, name } = getSplitSuborderTypeInfo(
      type,
      dockyard,
      project
    );
    return `${label}${code !== undefined ? ` : ${code} - ${name}` : ""}`;
  };

  const getSplitSuborderTypeInfo = (
    type: string,
    dockyard: any,
    project: any
  ): { code: string | undefined; label: string; name: string | undefined } => {
    const label = getSubOrderType(type);
    let code;
    let name;
    if (type === SuborderType.DOCKYARD && dockyard) {
      code = dockyard.code;
      name = dockyard.name;
    } else if (type === SuborderType.PROJECT && project) {
      code = project.code;
      name = project.name;
    }
    return {
      label,
      code,
      name,
    };
  };

  const defaultOptions: any = filter?._id
    ? {
        defaultFilters: { company: filter._id },
        defaultSort: "createdAt:desc",
        populate: [
          "suborders",
          "suborders.suborderProducts",
          "receptions",
          "receptions.files",
          "receptions.receptionReferences",
          "receptions.productQuantity",
          "files",
        ],
      }
    : {
        defaultSort: "createdAt:desc",
        populate: [
          "suborders",
          "suborders.suborderProducts",
          "receptions",
          "receptions.files",
          "receptions.receptionReferences",
          "receptions.productQuantity",
          "files",
        ],
      };

  const togglePopin = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  return {
    filter,
    classes,
    togglePopin,
    open,
    getOrderType,
    getNewOrderType,
    getSplitSuborderTypeInfo,
    getSubOrderType,
    getSuborderTypeInfo,
    defaultOptions,
    openSnackbar,
    dateStart_tfilter,
    setDateStart_tfilter,
    dateEnd_tfilter,
    setDateEnd_tfilter,
  };
};

export default useOrder;
